import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useCustomer } from "@/lib/customerAuth";
import { formatErr } from "@/lib/api";
import { Lock, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const { resetPassword } = useCustomer();
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!token) return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <AlertTriangle className="w-14 h-14 text-[#D71920] mx-auto" />
      <h1 className="font-display text-3xl uppercase mt-4">Missing token</h1>
      <p className="text-sm text-[#555] mt-3">This reset link is missing or malformed. Please request a new one.</p>
      <Link to="/forgot-password" className="ff-btn-primary mt-8">Request new link</Link>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      toast.success("Password reset — you're signed in");
      nav("/my-bookings");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold text-center">Set new password</div>
      <h1 className="font-display text-4xl uppercase mt-2 text-center">Reset password</h1>

      <form onSubmit={submit} className="ff-card p-8 mt-8 space-y-4" data-testid="reset-form">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666] font-bold">New password (min 8 chars)</label>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} className="ff-input pl-9" data-testid="reset-password" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666] font-bold">Confirm new password</label>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input type="password" required minLength={8} value={confirm} onChange={e => setConfirm(e.target.value)} className="ff-input pl-9" data-testid="reset-confirm" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="ff-btn-primary w-full" data-testid="reset-submit">
          {busy ? "Resetting…" : "Reset password"}
        </button>
      </form>

      <p className="text-center text-sm text-[#555] mt-6">
        <Link to="/login" className="text-[#D71920] font-bold hover:underline" data-testid="back-to-login">Back to Sign In</Link>
      </p>
    </div>
  );
}
